import { useMemo } from 'react';

/**
 * Mock data types for Pool Detail page (Tangor frontend)
 */

export type PoolDetailsData = {
  poolId: string;
  name: string;
  baseSymbol: string;
  quoteSymbol: string;
  feePercent: number;
  // Earn section
  totalApr: number;
  tradingFee: number;
  // Stats section
  tvl: number;
  tvlChange: number;
  volume24h: number;
  volume24hChange: number;
  fees24h: number;
  fees24hChange: number;
  // USDC Rewards
  hasUsdcRewards: boolean;
  rewardRate: number;
  // Price data
  currentPrice: number;
  priceChange: number;
  baseIconUrl?: string;
  quoteIconUrl?: string;
};

export type PoolTransaction = {
  id: string;
  type: 'swap' | 'add' | 'remove';
  totalValue: number;
  baseAmount: number;
  quoteAmount: number;
  timestamp: Date;
  walletAddress: string;
};

export type PriceDataPoint = {
  date: Date;
  value: number;
};

// Mock pool detail data
const MOCK_POOL_DETAILS: Record<string, PoolDetailsData> = {
  '1': {
    poolId: '1',
    name: 'tAPPL / USDC',
    baseSymbol: 'tAPPL',
    quoteSymbol: 'USDC',
    feePercent: 0.05,
    totalApr: 7.96,
    tradingFee: 0.05,
    tvl: 91_320_000,
    tvlChange: 1.04,
    volume24h: 10_000_000,
    volume24hChange: 1.04,
    fees24h: 10_000_000,
    fees24hChange: 1.04,
    hasUsdcRewards: true,
    rewardRate: 2.0,
    currentPrice: 245.32,
    priceChange: 1.35,
  },
  '2': {
    poolId: '2',
    name: 'tTSLA / USDC',
    baseSymbol: 'tTSLA',
    quoteSymbol: 'USDC',
    feePercent: 0.05,
    totalApr: 4.92,
    tradingFee: 0.05,
    tvl: 3_153_598.71,
    tvlChange: 2.32,
    volume24h: 53_304.2,
    volume24hChange: 1.5,
    fees24h: 53_304.2,
    fees24hChange: 1.5,
    hasUsdcRewards: true,
    rewardRate: 2.0,
    currentPrice: 412.87,
    priceChange: 2.15,
  },
  '3': {
    poolId: '3',
    name: 'tNVDA / USDC',
    baseSymbol: 'tNVDA',
    quoteSymbol: 'USDC',
    feePercent: 0.05,
    totalApr: 2.61,
    tradingFee: 0.05,
    tvl: 202_394.203,
    tvlChange: 0.87,
    volume24h: 33_457.37,
    volume24hChange: 0.54,
    fees24h: 33_457.37,
    fees24hChange: 0.54,
    hasUsdcRewards: true,
    rewardRate: 2.0,
    currentPrice: 138.45,
    priceChange: -0.82,
  },
  '4': {
    poolId: '4',
    name: 'tMETA / USDC',
    baseSymbol: 'tMETA',
    quoteSymbol: 'USDC',
    feePercent: 0.05,
    totalApr: 0.66,
    tradingFee: 0.05,
    tvl: 128_390.3,
    tvlChange: -0.23,
    volume24h: 27_000.0,
    volume24hChange: -1.2,
    fees24h: 27_000.0,
    fees24hChange: -1.2,
    hasUsdcRewards: true,
    rewardRate: 2.0,
    currentPrice: 582.14,
    priceChange: 0.45,
  },
  '5': {
    poolId: '5',
    name: 'EURC / USDC',
    baseSymbol: 'EURC',
    quoteSymbol: 'USDC',
    feePercent: 0.05,
    totalApr: 7.96,
    tradingFee: 0.05,
    tvl: 91_320_000,
    tvlChange: 1.04,
    volume24h: 10_000_000,
    volume24hChange: 1.04,
    fees24h: 10_000_000,
    fees24hChange: 1.04,
    hasUsdcRewards: true,
    rewardRate: 2.0,
    currentPrice: 1.14,
    priceChange: 1.35,
  },
  '6': {
    poolId: '6',
    name: 'ETH / USDC',
    baseSymbol: 'ETH',
    quoteSymbol: 'USDC',
    feePercent: 0.3,
    totalApr: 5.24,
    tradingFee: 0.3,
    tvl: 64_302.23,
    tvlChange: 0.02,
    volume24h: 1_234_567,
    volume24hChange: 3.21,
    fees24h: 4_127.88,
    fees24hChange: 2.14,
    hasUsdcRewards: true,
    rewardRate: 2.0,
    currentPrice: 3_245.67,
    priceChange: 2.34,
  },
  '7': {
    poolId: '7',
    name: 'cbBTC / ETH',
    baseSymbol: 'cbBTC',
    quoteSymbol: 'ETH',
    feePercent: 0.3,
    totalApr: 3.12,
    tradingFee: 0.3,
    tvl: 64_302.23,
    tvlChange: 0.02,
    volume24h: 987_654,
    volume24hChange: -0.54,
    fees24h: 4_127.88,
    fees24hChange: 0.87,
    hasUsdcRewards: false,
    rewardRate: 0,
    currentPrice: 15.23,
    priceChange: -0.12,
  },
  '8': {
    poolId: '8',
    name: 'cbBTC / USDC',
    baseSymbol: 'cbBTC',
    quoteSymbol: 'USDC',
    feePercent: 0.3,
    totalApr: 4.56,
    tradingFee: 0.3,
    tvl: 64_302.23,
    tvlChange: 0.02,
    volume24h: 2_345_678,
    volume24hChange: 1.87,
    fees24h: 4_127.88,
    fees24hChange: 1.23,
    hasUsdcRewards: true,
    rewardRate: 2.0,
    currentPrice: 97_234.56,
    priceChange: 1.45,
  },
  '9': {
    poolId: '9',
    name: 'EURC / USDC',
    baseSymbol: 'EURC',
    quoteSymbol: 'USDC',
    feePercent: 0.01,
    totalApr: 1.23,
    tradingFee: 0.01,
    tvl: 64_302.23,
    tvlChange: 0.02,
    volume24h: 543_210,
    volume24hChange: 0.32,
    fees24h: 4_127.88,
    fees24hChange: 0.15,
    hasUsdcRewards: true,
    rewardRate: 2.0,
    currentPrice: 1.08,
    priceChange: 0.05,
  },
};

// Generate mock price history data
function generateMockPriceData(basePrice: number, days = 7): PriceDataPoint[] {
  const data: PriceDataPoint[] = [];
  const now = new Date();
  const pointsPerDay = 24; // hourly data points
  const totalPoints = days * pointsPerDay;

  for (let i = totalPoints; i >= 0; i--) {
    const date = new Date(now.getTime() - i * 60 * 60 * 1000);
    // Add some random variation around the base price
    const variation = (Math.random() - 0.5) * basePrice * 0.1;
    const trendFactor = 1 + ((totalPoints - i) / totalPoints) * 0.05; // slight upward trend
    data.push({
      date,
      value: basePrice * trendFactor + variation,
    });
  }

  return data;
}

// Generate mock transactions
function generateMockTransactions(poolId: string): PoolTransaction[] {
  const types: PoolTransaction['type'][] = ['swap', 'add', 'remove'];
  const transactions: PoolTransaction[] = [];
  const now = new Date();

  for (let i = 0; i < 12; i++) {
    const type = types[Math.floor(Math.random() * types.length)];
    const totalValue = Math.random() * 50000 + 1000;
    const baseAmount = Math.random() * 100;
    const quoteAmount = totalValue - baseAmount * 100;

    transactions.push({
      id: `tx-${poolId}-${i}`,
      type,
      totalValue,
      baseAmount,
      quoteAmount,
      timestamp: new Date(now.getTime() - i * 15 * 60 * 1000), // 15 min intervals
      walletAddress: `0x${Math.random().toString(16).slice(2, 10)}...${Math.random()
        .toString(16)
        .slice(2, 6)}`,
    });
  }

  return transactions;
}

/**
 * Hook to get mock pool detail data
 */
export function usePoolDetailsData(poolId: string) {
  const pool = useMemo(() => {
    return MOCK_POOL_DETAILS[poolId] || MOCK_POOL_DETAILS['5']; // Default to EURC/USDC
  }, [poolId]);

  const priceHistory = useMemo(() => {
    return generateMockPriceData(pool.currentPrice);
  }, [pool.currentPrice]);

  const transactions = useMemo(() => {
    return generateMockTransactions(poolId);
  }, [poolId]);

  return {
    pool,
    priceHistory,
    transactions,
    isLoading: false,
  };
}
